/**
 * ContentPlanSection — assets grouped by type.
 *
 * Each group is a card with a big count and expandable breakdown.
 * Sprint 4 fence: no generation happens here; the card is purely a
 * plan display. Groups with zero total are hidden by the backend.
 */
import { useState } from "react";
import { Card } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { CONTENT_TYPE_LABEL } from "@/features/marketing-strategy/types";
import type { ContentPlan } from "../../types";

export function ContentPlanSection({
  plan,
}: {
  plan: ContentPlan;
}): JSX.Element {
  const [openKey, setOpenKey] = useState<string | null>(null);

  return (
    <section data-testid="room-content-plan">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Content Plan
        </p>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          {plan.total} assets planned
        </h2>
        <p className="text-sm text-muted-foreground mt-2">
          Grouped by content type. Every asset already has a channel and a purpose.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {plan.groups.map((g) => {
          const isOpen = openKey === g.key;
          return (
            <Card
              key={g.key}
              className="p-5"
              data-testid={`room-content-group-${g.key}`}
            >
              <div className="flex items-baseline justify-between mb-3">
                <span className="font-semibold">{g.label}</span>
                <span
                  className="text-3xl font-semibold tabular-nums"
                  data-testid={`room-content-count-${g.key}`}
                >
                  {g.total}
                </span>
              </div>
              {g.breakdown.length > 1 && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="mt-1 -ml-2 h-8 text-xs"
                  onClick={() => setOpenKey(isOpen ? null : g.key)}
                  data-testid={`room-content-toggle-${g.key}`}
                >
                  {isOpen ? (
                    <>
                      <ChevronUp className="h-3.5 w-3.5 mr-1" aria-hidden /> Hide detail
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-3.5 w-3.5 mr-1" aria-hidden /> Show detail
                    </>
                  )}
                </Button>
              )}
              {isOpen && (
                <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                  {g.breakdown.map((b) => (
                    <li
                      key={b.content_type}
                      className="flex items-center justify-between"
                    >
                      <span>{CONTENT_TYPE_LABEL[b.content_type] ?? b.content_type}</span>
                      <span className="tabular-nums">{b.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          );
        })}
      </div>
    </section>
  );
}
